import { find } from 'ramda';
import { PH_PROGRAM_NAME } from '../GeneralAffordable/constants';

const getFloorPlanPrograms = (application) =>
  application?.au?.unit?.floorPlan?.floorPlanAffordablePrograms || null;

const getAffordableQualifications = (application) =>
  application?.affordableQualifications || null;

const getAffordableQualificationHistories = (application) =>
  application?.affordableQualificationHistories || null;

const getPropertyAffordableProgramId = (fpProgram) =>
  fpProgram?.propertyAffordableProgram?.id || null;

const getStatusText = (qualMatchingFpProgram) =>
  qualMatchingFpProgram?.complianceApproval?.name || 'Not Completed';

const findMatchingQualification = (
  propertyAffordableProgramId,
  affordableQualifications,
) =>
  find(
    (qual) =>
      qual?.propertyAffordableProgram?.id === propertyAffordableProgramId,
    affordableQualifications,
  );

const determineStatusValidity = (statusText) =>
  ['Approved', 'Approved Pending Signatures'].includes(statusText);

const getProgramName = (qualMatchingFpProgram) =>
  qualMatchingFpProgram?.propertyAffordableProgram?.masterAffordableProgram
    ?.name || '';

const generateComplianceStatus = (valid, title, statusText) =>
  valid
    ? { title, valid, statusText }
    : { title, valid, statusText, error: 'Must have Compliance Approval' };

const getMatchingQualification = (fpProgram, affordableQualifications) => {
  const propertyAffordableProgramId = getPropertyAffordableProgramId(fpProgram);
  if (!propertyAffordableProgramId) return null;

  return findMatchingQualification(
    propertyAffordableProgramId,
    affordableQualifications,
  );
};

const complianceStatusReducer =
  (affordableQualifications) => (statuses, fpProgram) => {
    const qualMatchingFpProgram = getMatchingQualification(
      fpProgram,
      affordableQualifications,
    );
    if (!qualMatchingFpProgram) return statuses;
    const statusText = getStatusText(qualMatchingFpProgram);
    const valid = determineStatusValidity(statusText);
    const title = `Compliance Approval on ${getProgramName(
      qualMatchingFpProgram,
    )} Qualification Tab`;

    return [...statuses, generateComplianceStatus(valid, title, statusText)];
  };

const generateCompletionStatus = (valid, title, statusText) =>
  valid
    ? { title, valid, statusText }
    : { title, valid, statusText, error: 'Must have a Completed Certificate' };

const completionStatusReducer =
  (affordableQualifications) => (statuses, fpProgram) => {
    const qualMatchingFpProgram = getMatchingQualification(
      fpProgram,
      affordableQualifications,
    );
    if (!qualMatchingFpProgram) return statuses;

    // A cert with a certificationCompletionDate was completed at some point,
    // even if it was later reopened and its status changed to REOPEN.
    const valid = qualMatchingFpProgram?.certificationCompletionDate != null;
    const statusText = valid ? 'Completed' : 'Not Completed';
    const title = `Certificate Completion on ${getProgramName(
      qualMatchingFpProgram,
    )} Qualification Tab`;

    return [...statuses, generateCompletionStatus(valid, title, statusText)];
  };

const isHybridProgramPresent = (programName, floorPlanPrograms) => {
  const programParts = programName.split('/');
  return programParts.every((part) =>
    floorPlanPrograms.some(
      (fpap) =>
        fpap?.propertyAffordableProgram?.masterAffordableProgram?.name === part,
    ),
  );
};

export const generateComplianceStatusArray = (
  application,
  propertyAffordablePrograms,
) => {
  const floorPlanPrograms = getFloorPlanPrograms(application);
  if (!floorPlanPrograms) return [];

  const affordableQualifications = getAffordableQualifications(application);
  const affordableQualificationHistories =
    getAffordableQualificationHistories(application);
  const affordableQualificationsWithHistory = (
    affordableQualifications || []
  ).concat(affordableQualificationHistories);

  const hybridPrograms = propertyAffordablePrograms?.filter((pap) =>
    pap?.masterAffordableProgram?.name.includes('/'),
  );

  let fpPrograms = floorPlanPrograms;

  hybridPrograms?.forEach((hybridProgram) => {
    const hybridProgramName = hybridProgram?.masterAffordableProgram?.name;
    if (isHybridProgramPresent(hybridProgramName, floorPlanPrograms)) {
      fpPrograms = [{ propertyAffordableProgram: hybridProgram }];
    }
  });

  const complianceApproval = fpPrograms.reduce(
    complianceStatusReducer(affordableQualificationsWithHistory),
    [],
  );

  const programsRequiringCompletion = fpPrograms.filter((program) =>
    program?.propertyAffordableProgram?.masterAffordableProgram?.name.includes(
      PH_PROGRAM_NAME,
    ),
  );

  const certificateCompletion = programsRequiringCompletion.reduce(
    completionStatusReducer(affordableQualificationsWithHistory),
    [],
  );

  return [...complianceApproval, ...certificateCompletion];
};
